import { useCallback, useEffect, useRef, useState } from "react";
import {
  FEEDBACK_PROMPT_THRESHOLD_MS,
  creditFeedbackPromptDwell,
  isFeedbackPromptDismissed,
  markFeedbackPromptDismissed,
  pauseFeedbackPromptDwell,
  readFeedbackPromptState,
} from "@/lib/feedback/feedbackPromptStorage";

const TICK_MS = import.meta.env.DEV ? 1_000 : 5_000;

/**
 * Tracks active (visible-tab) dwell time for a logged-in user and reports when
 * the soft feedback prompt should appear.
 */
export function useFeedbackPromptDwell(userId: string | undefined, enabled: boolean) {
  const [shouldShow, setShouldShow] = useState(false);
  const dismissedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !userId) {
      dismissedRef.current = false;
      setShouldShow(false);
      return;
    }

    dismissedRef.current = isFeedbackPromptDismissed(userId);
    if (dismissedRef.current) {
      setShouldShow(false);
      return;
    }

    const evaluate = () => {
      if (dismissedRef.current || isFeedbackPromptDismissed(userId)) {
        dismissedRef.current = true;
        setShouldShow(false);
        return;
      }
      const { accumulatedMs } = readFeedbackPromptState(userId);
      if (accumulatedMs >= FEEDBACK_PROMPT_THRESHOLD_MS) {
        setShouldShow(true);
      }
    };

    evaluate();

    const onVisibleTick = () => {
      if (document.visibilityState !== "visible") return;
      if (dismissedRef.current || isFeedbackPromptDismissed(userId)) {
        dismissedRef.current = true;
        setShouldShow(false);
        return;
      }
      const accumulated = creditFeedbackPromptDwell(userId);
      if (dismissedRef.current) return;
      if (accumulated >= FEEDBACK_PROMPT_THRESHOLD_MS) {
        setShouldShow(true);
      }
    };

    const onVisibilityChange = () => {
      if (dismissedRef.current) return;
      pauseFeedbackPromptDwell(userId);
    };

    const intervalId = window.setInterval(onVisibleTick, TICK_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);
    const immediateId = window.setTimeout(onVisibleTick, 250);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(immediateId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [userId, enabled]);

  const dismiss = useCallback(() => {
    dismissedRef.current = true;
    if (userId) markFeedbackPromptDismissed(userId);
    setShouldShow(false);
  }, [userId]);

  return { shouldShow, dismiss };
}
